<style lang="scss" scoped>
.disabled-wrapper {
    pointer-events: none;
    opacity: 0.3;
}

.format-wrapper {
    position: relative;
    min-height: 64px;
    overflow: visible;

    .format-row {
        @media screen and (max-width: 767.98px) {
            position: absolute;
            z-index: 5;
            left: 0;
            padding-right: 40px;
        }
    }
}

::v-deep .flatpickr-calendar {
    @media screen and (max-width: 767.98px) {
        margin-left: auto;
        margin-right: auto;
    }
}
</style>

<template>
    <div class="popup px-16 pt-24 p-md-24 p-lg-56" ref="fixBottom">
        <div class="close" @click="$emit('close')"></div>
        <h2 class="mb-16 mb-md-32">{{ translations.choose_date_popup.choose_date }}</h2>
        <p class="mb-32">{{ translations.choose_date_popup.choose_date_description }}</p>

        <div class="format-wrapper">
            <div class="d-flex align-items-center format-row mb-32">
                <a class="d-inline-block flex-shrink-0 mr-24" @click.prevent="setFormat('single')" :class="{'active': format === 'single', 'pl-0': format !== 'single'}">{{ translations.choose_date_popup.choose_date_tab_label }}</a>
                <a class="d-inline-block flex-shrink-0 mr-24" @click.prevent="setFormat('multiple')" :class="{'active': format === 'multiple'}">{{ translations.choose_date_popup.multiple_dates }}</a>
                <a class="d-inline-block flex-shrink-0 mr-24" @click.prevent="setFormat('period')" :class="{'active': format === 'period'}">{{ translations.choose_date_popup.date_period }}</a>
                <a class="d-inline-block flex-shrink-0 " :class="{'active': format === 'unknown'}" @click.prevent="setFormat('unknown')">{{ translations.choose_date_popup.unknown_date }}</a>
            </div>
        </div>

        <div class="mb-32">
            <template v-if="format === 'single'">
                <div>
                    <flat-pickr :allowInput="false" id="date-multiple" name="date-multiple" v-model="flatPickrDate" :config="singleConfig"></flat-pickr>
                </div>
            </template>

            <template v-if="format === 'multiple'">
                <div>
                    <flat-pickr :allowInput="false" id="date-multiple" name="date-multiple" v-model="flatPickrDate" :config="multipleConfig"></flat-pickr>
                </div>
            </template>

            <template v-if="format === 'period'">
                <div>
                    <flat-pickr :allowInput="false" id="date-period" name="date-period" v-model="flatPickrDate" :config="periodConfig"></flat-pickr>
                </div>
            </template>

            <template v-if="format === 'unknown'">
                <div class="disabled-wrapper">
                    <flat-pickr :allowInput="false" id="date-unknown" name="date-unknown" v-model="flatPickrDate" :config="unknownConfig"></flat-pickr>
                </div>
            </template>
        </div>

        <div class="popup-footer d-flex align-items-center">
            <button class="btn btn-accent btn-sm-100 text-white" @click="submit" v-html="translations.choose_date_popup.select"></button>
        </div>
    </div>
</template>

<script>
import core from "../day-configurator-core";
import safariFixedBottom from "../../mixins/safari-fixed-bottom";
import { SlideYDownTransition } from 'vue2-transitions';
import flatPickr from 'vue-flatpickr-component';
import { Dutch } from 'flatpickr/dist/l10n/nl.js';
import { bus } from '@/app';

export default {
    mixins: [core, safariFixedBottom],

    components: { SlideYDownTransition, flatPickr },

    props: {
        localSchedule: {
            type: Object,
            required: true
        }
    },

    data: () => ({
        format: "multiple",
        flatPickrDate: "",

        singleConfig: {
            locale: Dutch,
            mode: "single",
            inline: true,
            minDate: new Date(),
            dateFormat: "Y-m-d",
            showMonths: document.documentElement.clientWidth >= 768 ? 2 : 1
        },

        multipleConfig: {
            locale: Dutch,
            mode: "multiple",
            inline: true,
            minDate: new Date(),
            dateFormat: "Y-m-d",
            showMonths: document.documentElement.clientWidth >= 768 ? 2 : 1
        },

        periodConfig: {
            locale: Dutch,
            mode: "range",
            inline: true,
            minDate: new Date(),
            dateFormat: "Y-m-d",
            showMonths: document.documentElement.clientWidth >= 768 ? 2 : 1
        },

        unknownConfig: {
            locale: Dutch,
            mode: "single",
            inline: true,
            minDate: new Date(),
            dateFormat: "Y-m-d",
            showMonths: document.documentElement.clientWidth >= 768 ? 2 : 1
        },
    }),

    created() {
        this.readSchedule();
    },

    methods: {
        readSchedule() {
            if (this.localSchedule.date_object != null) {
                this.parseDate(this.localSchedule.date_object);
            }
        },

        parseDate(date_object) {
            let dateString = "";

            if (date_object.type === "single") {
                dateString = date_object.data.date;
            }
            else if (date_object.type === "multiple") {
                dateString = date_object.data.dates.join(', ');
            }
            else if (date_object.type === "period") {
                dateString = `${date_object.data.start} tot ${date_object.data.end}`;
                this.format = "period";
            }

            this.flatPickrDate = dateString;
        },

        parseFlatPickrDate(flatPickrDate) {
            let date_object = new Object();

            if (flatPickrDate.includes(" tot ")) {
                date_object.type = "period";

                let dateArray = flatPickrDate.split(" tot ");
                date_object.data = {
                    start: dateArray[0],
                    end: dateArray[1]
                }
            } else {
                let dateArray = flatPickrDate.split(", ");

                if (flatPickrDate == '' || dateArray.length === 0) {
                    date_object.type = "unknown";
                } else if (dateArray.length === 1) {
                    date_object.type = "single";
                    date_object.data = {
                        date: dateArray[0]
                    }
                } else if (dateArray.length > 1) {
                    date_object.type = "multiple";
                    date_object.data = {
                        dates: dateArray
                    }
                }
            }

            return date_object;
        },

        submit() {
            bus.$emit('dateUpdate', this.parseFlatPickrDate(this.flatPickrDate));
            this.$emit('close');
        },

        setFormat(format) {
            this.flatPickrDate = '';
            this.format = format;
        }
    }
}
</script>